import { MarketAccountSelectionList } from '@src/components/MarketAccountSelectionList';
import { useSecuritiesContext } from '@src/providers';
import { HStack, isMultileg, useCallbackRef } from '@talos/kyoko';
import { useCallback, useMemo, useState, type RefObject } from 'react';
import { useTheme } from 'styled-components';
import { v1 as uuid } from 'uuid';

import { MarketAccountsWrapper, SymbolSelector } from './styles';

import {
  Box,
  Button,
  ButtonActions,
  ButtonGroup,
  ButtonVariants,
  CardViewEnum,
  ConnectionType,
  Divider,
  FormControlSizes,
  FormGroup,
  IconButton,
  IconName,
  Input,
  MarketDataCardFormWrapper,
  MarketDataCardState,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  ProductTypeEnum,
  Toggle,
  UnifiedLiquidityEnum,
  UnifiedLiquidityTooltip,
  useConstant,
  useMixpanel,
  useSecurity,
  useUnifiedLiquidity,
  useUnifiedLiquidityParameter,
  type MarketDataCardConfig,
} from '@talos/kyoko';
import { useIsMobileLayout } from '../../hooks/useIsMobileLayout';
import { useNavigateToMultilegCombo } from '../../hooks/useNavigateToMultilegCombo';
import { MULTILEG_MARKET_ACCOUNT } from '../../providers/OMSContext.types';
import { useTradingSettings } from '../../providers/TradingSettingsContext';
import { usePanelContext } from '../Layout/providers/PanelContextProvider';
import { MarketSelector } from '../MarketSelector/MarketSelector';
import { MultiLegSummary } from '../MultiLegSummary';
import { MultilegComboType } from '../MultilegCombo/enums';
import { getMultilegInstrumentType } from '../OMS/NewOrder/utils/mappers';
import { useSymbolSelectorWidth } from '../SymbolSelector/useSymbolSelectorWidth';
import { SettingsRelatedActionsButton } from './SettingsRelatedActionsButton';
import { useMarketDataCardSettings } from './useMarketDataCardSettings';

interface MarketDataCardSettingsProps {
  config: MarketDataCardConfig;
  onClose: () => void;
  saveCard: (config: MarketDataCardConfig) => void;
  deleteCard: (config: MarketDataCardConfig) => void;
  hidePlaceholderCards: () => void;
  cardRef: RefObject<HTMLDivElement>;
}

export function MarketDataCardSettings({
  config,
  onClose,
  saveCard,
  deleteCard,
  hidePlaceholderCards,
  cardRef,
}: MarketDataCardSettingsProps) {
  const { useNewMarketSelector } = useTradingSettings();
  const mixpanel = useMixpanel();
  const { spacing4 } = useTheme();
  const dropdownWidth = useSymbolSelectorWidth();
  const { isPopout } = usePanelContext();
  const isInMobileMode = useIsMobileLayout();
  const navigateToMultilegCombo = useNavigateToMultilegCombo();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    form,
    ulEnabled,
    enableShowBBO,
    setShowBBO,
    openMarketAccountSelector,
    onSymbolChange,
    updateForm,
    marketSelector,
    oldMarketSelectorItems,
    onChangeUnifiedLiquidity,
    onMultilegULMarketsChange,
    onMarketAccountsChange,
    onMarketsChange,
  } = useMarketDataCardSettings({ config });

  const { securitiesBySymbol } = useSecuritiesContext();
  const security = useSecurity(form.symbol);
  // Default the state to UPDATED_CARD for... some reason. Maybe backwards compat from a long time ago.
  const { state = MarketDataCardState.UPDATED_CARD } = config;

  const unifiedLiquidityToken = useUnifiedLiquidity(form.symbol);
  const showUnifiedLiquidityOption = !!unifiedLiquidityToken;

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      setIsSubmitting(true);
      e.preventDefault();

      let newConfig: MarketDataCardConfig = {
        ...config,
        ...form,
        // After setting a new config, all markets are selected by default for the 3rd slide
        availableMarketAccounts: [...form.selectedMarketAccounts],
        selectedMarketAccounts: [...form.selectedMarketAccounts],
        availableMarkets: [...(form.selectedMarkets ?? [])],
        selectedMarkets: [...(form.selectedMarkets ?? [])],
        selectedAggregation: form.selectedAggregation,
        detailsExpanded: security?.ProductType === ProductTypeEnum.Spot ? false : config.detailsExpanded,
        unifiedLiquidity: ulEnabled ? UnifiedLiquidityEnum.Enabled : UnifiedLiquidityEnum.Disabled,
        alwaysDisplayBestBidOffer: enableShowBBO,
      };

      if (newConfig.state == null) {
        newConfig.state = MarketDataCardState.UPDATED_CARD;
      }

      if (securitiesBySymbol && config.symbol !== form.symbol) {
        const security = form.symbol != null && securitiesBySymbol.get(form.symbol);
        if (security) {
          newConfig = {
            ...newConfig,
            id: config.id || uuid(),
            priceIncrement: security.DefaultPriceIncrement,
            sizeBuckets: security.SizeBuckets,
            symbol: security.Symbol,
            orderPresetID: undefined,
            topOfBookSizeBucket: security.NormalSize,
          };
        }
      }
      // Track usage of unified liquidity card
      if (
        // Unified liquidity was updated
        config.unifiedLiquidity !== newConfig.unifiedLiquidity &&
        newConfig.unifiedLiquidity !== undefined
      ) {
        mixpanel.track(MixpanelEvent.SetMarketCardUnifiedLiquidity, {
          [MixpanelEventProperty.Enabled]: newConfig.unifiedLiquidity,
        });
      }
      // Track usage of titles
      if (newConfig.title?.length && config.title !== newConfig.title) {
        mixpanel.track(MixpanelEvent.SetMarketCardTitle);
      }
      // Track usage of compact view
      if (config.view !== newConfig.view) {
        mixpanel.track(MixpanelEvent.SetMarketCardView, {
          [MixpanelEventProperty.View]: newConfig.view,
        });
      }

      saveCard(newConfig);
      onClose();
    },
    [config, form, mixpanel, onClose, saveCard, securitiesBySymbol, security?.ProductType, ulEnabled, enableShowBBO]
  );

  const invalidMarketAccounts = form.selectedMarketAccounts.length === 0;
  const invalidMarkets = form.selectedMarkets == null || form.selectedMarkets.length === 0;
  const primaryButtonDisabled = isSubmitting || security == null || (invalidMarketAccounts && invalidMarkets);

  const handleDeleteCard = useCallback(() => {
    deleteCard(config);
    if (state === MarketDataCardState.NEW_CARD) {
      hidePlaceholderCards();
    }
  }, [config, state, deleteCard, hidePlaceholderCards]);

  const symbolSelectorStartOpen = useConstant(form.symbol == null);
  const unifiedLiquidityParameter = useUnifiedLiquidityParameter();
  const handleOpenEditMultileg = useCallbackRef((_symbol: string) => {
    if (!security) {
      return;
    }
    mixpanel.track(MixpanelEvent.EditMultileg, {
      [MixpanelEventProperty.Source]: MixpanelEventSource.MarketCard,
    });
    navigateToMultilegCombo(
      getMultilegInstrumentType(security.MultilegDetails?.SyntheticProductType) ?? MultilegComboType.SyntheticCross,
      security
    );
  });

  const multilegULLegMarketAccounts = useMemo(
    () => form.selectedMarketAccounts.filter(ma => ma !== MULTILEG_MARKET_ACCOUNT),
    [form.selectedMarketAccounts]
  );

  return (
    <>
      <MarketDataCardFormWrapper onSubmit={handleSubmit}>
        <FormGroup
          size={FormControlSizes.Small}
          label="Symbol"
          inline
          mb="spacing8"
          labelWidth="6rem"
          alignItems="center"
        >
          <Box data-testid="market-card-settings-symbol-selector">
            <SymbolSelector
              symbol={form.symbol}
              onSymbolChanged={onSymbolChange}
              startOpen={symbolSelectorStartOpen}
              size={FormControlSizes.Small}
              dropdownWidth={dropdownWidth}
              dropdownPlacement="bottom-end"
              showGroups
              showFavorites
              portalize
            />
          </Box>
          {state === MarketDataCardState.UPDATED_CARD && (
            <IconButton
              ghost
              icon={IconName.Close}
              onClick={onClose}
              style={{ marginLeft: spacing4 }}
              size={FormControlSizes.Small}
              data-testid="market-card-settings-slide-close"
            />
          )}
        </FormGroup>
        <FormGroup size={FormControlSizes.Small} label="Title" inline mb="spacing8" controlId="title" labelWidth="6rem">
          <Input
            id="title"
            name="title"
            value={form.title ?? ''}
            placeholder="Custom card title (optional)"
            size={FormControlSizes.Small}
            onChange={e => updateForm({ title: e.target.value })}
          />
        </FormGroup>
        {!isInMobileMode && (
          <FormGroup
            size={FormControlSizes.Small}
            label="Card View"
            inline
            mb="spacing8"
            controlId="title"
            labelWidth="6rem"
          >
            <ButtonGroup size={FormControlSizes.Small} flex="1">
              <Button
                data-testid="market-card-settings-card-view-normal"
                data-selected={form.view === CardViewEnum.NORMAL}
                onClick={() => updateForm({ view: CardViewEnum.NORMAL })}
                variant={form.view === CardViewEnum.NORMAL ? ButtonVariants.Priority : ButtonVariants.Default}
              >
                Normal
              </Button>
              <Button
                data-testid="market-card-settings-card-view-compact"
                data-selected={form.view === CardViewEnum.COMPACT}
                onClick={() => updateForm({ view: CardViewEnum.COMPACT })}
                variant={form.view === CardViewEnum.COMPACT ? ButtonVariants.Priority : ButtonVariants.Default}
              >
                Compact
              </Button>
            </ButtonGroup>
          </FormGroup>
        )}
        <HStack gap="spacing8" justifyContent="space-between">
          <FormGroup
            size={FormControlSizes.Small}
            label="Show BBO"
            tooltip="Always show the Best Bid Offer rate at the top of the market card, regardless of the entered Qty."
            inline
            mb="spacing8"
            controlId="title"
            labelWidth="4rem"
            alignItems="center"
          >
            <Toggle
              size={FormControlSizes.Default}
              checked={enableShowBBO}
              onChange={(value: boolean) => setShowBBO(value)}
            />
          </FormGroup>
          {security && form.symbol && showUnifiedLiquidityOption && unifiedLiquidityParameter && (
            <FormGroup
              size={FormControlSizes.Small}
              label={unifiedLiquidityParameter.DisplayName}
              tooltip={
                <UnifiedLiquidityTooltip
                  security={security}
                  marketAccounts={form.selectedMarketAccounts}
                  markets={form.selectedMarkets}
                  unifiedLiquidityParameter={unifiedLiquidityParameter}
                  showDescription={true}
                />
              }
              inline
              mb="spacing8"
              controlId="title"
              labelWidth="6rem"
              alignItems="center"
            >
              <Toggle
                size={FormControlSizes.Default}
                checked={ulEnabled}
                onChange={onChangeUnifiedLiquidity}
                data-testid="market-card-settings-unified-liquidity-toggle"
              />
            </FormGroup>
          )}
        </HStack>

        {security ? (
          isMultileg(security) ? (
            <MarketAccountsWrapper>
              <MultiLegSummary
                security={security}
                readOnly={false}
                supportsAllMarkets={true}
                touched={true}
                unifiedLiquidityToken={ulEnabled ? unifiedLiquidityToken : undefined}
                unifiedLiquidityMarkets={multilegULLegMarketAccounts}
                onEditSymbol={handleOpenEditMultileg}
                onUnifiedLiquidityMarketsChange={onMultilegULMarketsChange}
                allocations={-1}
              />
            </MarketAccountsWrapper>
          ) : useNewMarketSelector ? (
            <>
              <Divider orientation="horizontal" mb="spacing8" />
              <MarketSelector
                size={FormControlSizes.Small}
                marketSelectorAPI={marketSelector}
                forceOpen={openMarketAccountSelector}
                showCredentialsOnlyToggle
                panelOffset={8}
                panelPlacement="auto"
                panelAnchorRef={cardRef}
                isNarrow={isPopout || isInMobileMode}
              />
            </>
          ) : (
            <MarketAccountsWrapper>
              <MarketAccountSelectionList
                availableMarkets={oldMarketSelectorItems.markets}
                availableMarketAccounts={oldMarketSelectorItems.marketAccounts}
                selectedMarketAccounts={form.selectedMarketAccounts}
                selectedMarkets={form.selectedMarkets}
                security={security}
                onChangeMarketAccounts={onMarketAccountsChange}
                onChangeMarkets={onMarketsChange}
                connectionType={ConnectionType.MarketData}
                showInactiveMarketAccounts={true}
              />
            </MarketAccountsWrapper>
          )
        ) : (
          <Box flex="1" />
        )}

        <ButtonActions gap="spacing8">
          <SettingsRelatedActionsButton
            selectedMarketAccounts={form.selectedMarketAccounts}
            selectedMarkets={form.selectedMarkets}
            symbol={form.symbol}
          />

          <Button
            size={FormControlSizes.Small}
            onClick={handleDeleteCard}
            variant={state === MarketDataCardState.NEW_CARD ? ButtonVariants.Priority : ButtonVariants.Negative}
            data-testid="market-data-card-settings-delete-button"
            flex="1"
          >
            {state === MarketDataCardState.NEW_CARD ? `Cancel` : `Delete Card`}
          </Button>
          <Button
            size={FormControlSizes.Small}
            variant={state === MarketDataCardState.NEW_CARD ? ButtonVariants.Positive : ButtonVariants.Primary}
            type="submit"
            disabled={primaryButtonDisabled}
            data-testid="market-data-card-settings-update-button"
            flex="1"
          >
            {isSubmitting
              ? state === MarketDataCardState.NEW_CARD
                ? `Creating...`
                : `Saving...`
              : state === MarketDataCardState.NEW_CARD
                ? `Create a Card`
                : `Update`}
          </Button>
        </ButtonActions>
      </MarketDataCardFormWrapper>
    </>
  );
}
